import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Briefcase, Users, Building2, LogOut, FileText, UserSquare2, Wrench, Mail, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Logo from '../../components/Logo';

const CMSLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/projects', icon: Building2, label: 'Projects' },
    { path: '/admin/team', icon: Users, label: 'Team' },
    { path: '/admin/services', icon: Wrench, label: 'Services' },
    { path: '/admin/careers', icon: Briefcase, label: 'Careers' },
    { path: '/admin/applications', icon: FileText, label: 'Applications' },
    { path: '/admin/contact-submissions', icon: Mail, label: 'Contact Forms' },
    { path: '/admin/quote-requests', icon: DollarSign, label: 'Quote Requests' },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-[#214979] to-[#214979]/90 text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Logo inverted={true} variant="icon" />
          <p className="text-xs text-[#c0932a]/80 mt-3">Admin Dashboard</p>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-secondary text-primary font-semibold'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium">{user.full_name}</p>
            <p className="text-xs text-white/60">ADMIN</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-white hover:bg-white/10"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 min-h-screen">
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {navItems.find(item => item.path === location.pathname)?.label || 'Admin Panel'}
          </h1>
        </header>
        
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default CMSLayout;
